import {
  Component,
  computed,
  inject,
  input,
  output,
  type OnInit,
} from '@angular/core';
import type { TypedComponentRendering } from '@core/jss-models';
import { Status } from '@essent/common';
import type { InstallationAddress } from '@essent/installation-address';
import type { FullCalculateFormValues } from '@innogy/become-a-customer/shared';
import {
  CalculateComponentContent,
  type CalculateComponentRendering,
} from '@innogy/become-a-customer/shared/interfaces';
import { enableCalculateManualHouseNumberAdditionInputAction } from '@innogy/become-a-customer/state';
import {
  CommonUiFormsModule,
  type DropdownItem,
} from '@innogy/common-ui/forms';
import {
  uppercaseConverter,
  AutocompleteDirective,
  IsInvalidPipe,
} from '@innogy/utils-deprecated';
import { Store } from '@ngrx/store';
import { FormGroupControls, type FormGroupState } from 'ngrx-forms';
import {
  TrackLinkDirective,
  TrackValueChangeDirective,
} from '@analytics/events';
import { CommonModule } from '@angular/common';
import { LoadingComponent } from '@legacy/common-ui-shared';
import { InfoCardComponent } from '@legacy/common-ui-utility-components';
import { CrntIcon } from '@essent/current-web-angular';
import { AutomationIdDirective } from '@innogy/utils-automation-id';

@Component({
  selector: 'wl-calculate-form-address',
  templateUrl: './calculate-form-address.component.html',
  styleUrls: ['./calculate-form-address.scss'],
  standalone: true,
  imports: [
    CommonModule,
    CommonUiFormsModule,
    AutomationIdDirective,
    AutocompleteDirective,
    IsInvalidPipe,
    TrackLinkDirective,
    TrackValueChangeDirective,
    CrntIcon,
    InfoCardComponent,
    LoadingComponent,
  ],
})
export class CalculateFormAddressComponent implements OnInit {
  public readonly uppercaseConverter = uppercaseConverter;

  readonly store = inject(Store);

  readonly rendering =
    input<TypedComponentRendering<CalculateComponentRendering>>();
  readonly content = input.required<CalculateComponentContent>();
  readonly controls =
    input.required<FormGroupControls<FullCalculateFormValues>>();
  readonly formState =
    input.required<FormGroupState<FullCalculateFormValues>>();
  readonly houseNumberAdditions = input<DropdownItem[]>();
  readonly manualHouseNumberAdditionInput = input<boolean>();
  readonly installationAddressStatus = input<Status>();
  readonly installationAddressNotFound = input<boolean>();
  readonly isAddressManualTriggerActive = input<boolean>();
  readonly isAlternateAddressEntryEnabled = input<boolean>();
  readonly installationAddresses = input<InstallationAddress[]>();

  hideManualHouseNumberAdditionInput = output();
  setAddressNextButtonClicked = output();
  updateAddressButtonClicked = output();

  readonly showLoader = computed(
    () => this.installationAddressStatus() === Status.PENDING
  );

  readonly showProvidedInstallationAddress = computed(() => {
    const address = this.installationAddresses()?.[0];
    const controls = this.controls();
    return address
      ? `${address.street} ${address.houseNumber}${controls.houseNumberAddition.value ?? ''}, ${address.city}`
      : `${controls.postalCode.value ?? ''} ${controls.houseNumber?.value ?? ''}${controls.houseNumberAddition.value ?? ''}`;
  });

  /**
   * Should only show the message when the message has been filled in in Sitecore (and when the API returns a 404)
   */
  readonly showAddressNotFoundMessage = computed(() => {
    return (
      this.installationAddressNotFound() &&
      !!(
        this.content().addressNotFoundErrorTitle ||
        this.content().addressNotFoundErrorMessage
      )
    );
  });

  ngOnInit() {
    if (this.content().useSupervisedManualHouseNumberAddition) {
      this.store.dispatch(
        enableCalculateManualHouseNumberAdditionInputAction()
      );
    }
  }
}
